import { Dropdown } from 'primereact/dropdown';
import moment from 'moment';

const FilterDropdown = ({
    className = 'activity-dropdown',
    value,
    options,
    onChange,
    optionLabel,
    optionValue,
    placeholder,
    id,
    style = { textAlign: 'left', width: '100%' },
    containerClass = 'p-col-12 p-md-3 filters',
    containerStyle,
    showHint = false,
    hintText = '',
    hintClass = 'reporting_period_hint',
    transformOptions,
    autoSetActiveId = false,
    activeIdSuffix = 'active_dropdown',
}) => {
    const processedOptions = transformOptions
        ? options.map(transformOptions)
        : options;


    return (
        <div className={containerClass} style={containerStyle}>
            <div className="p-inputgroup">
                <Dropdown
                    className={className}
                    value={value}
                    id={id || "periodtype"}
                    options={processedOptions}
                    onChange={onChange}
                    optionLabel={optionLabel}
                    optionValue={optionValue}
                    placeholder={placeholder}
                    style={style}
                    panelClassName="paytype_panel"
                />
            </div>
            {showHint && hintText && (
                <p className={hintClass}>{hintText}</p>
            )}
        </div>
    );
};

export default FilterDropdown;

export const DATE_PERIOD_OPTIONS = [
    { code: "today", name: "Today" },
    { code: "this_month", name: "This Month" },
    { code: "last_week", name: "Last Week" },
    { code: "last_month", name: "Last Month" },
    { code: "last_3_months", name: "Last 3 Months" },
    { code: "last_6_months", name: "Last 6 Months" },
    { code: "last_12_months", name: "Last 12 Months" },
    { code: "year_to_date", name: "Year to date" },
    { code: "lifetime", name: "Lifetime" },
    { code: "custom", name: "Custom Date" },
];

export const createdDateOf = (entity) => {
    if (entity == null || entity === '') return null;
    if (entity instanceof Date || typeof entity === 'string' || typeof entity === 'number') return entity;
    return entity.createdOn || entity.createdDate || entity.addedOn || null;
};

export const resolveLifetimeStart = ({ selected, graphed, fallbackDate } = {}) => {
    if (selected) return createdDateOf(selected) || fallbackDate || null;

    const dates = [];
    (graphed?.groups || []).forEach((item) => {
        dates.push(createdDateOf(item?.group || item));
        (item?.plans || []).forEach((plan) => dates.push(createdDateOf(plan)));
    });
    if (fallbackDate) dates.push(fallbackDate);

    const valid = dates
        .map((value) => (value ? moment(value) : null))
        .filter((value) => value && value.isValid());
    if (!valid.length) return null;
    return moment.min(valid).toDate();
};

export const getChartSeriesType = (periodCode) => {
    const code = typeof periodCode === 'object' ? periodCode?.code : periodCode;

    switch (code) {
        case 'today':
        case 'this_month':
        case 'last_week':
        case 'last_month':
            return 'DAILY';
        default:
            return 'MONTHLY';
    }
};

export const getDateRange = (period, options = {}) => {
    const { returnRange = false } = options;

    const periodCode = typeof period === 'object' && period?.code
        ? period.code
        : period;

    if (!periodCode) {
        return returnRange
            ? { startDate: moment().toDate(), endDate: moment().toDate() }
            : moment().toDate();
    }

    const normalizedCode = typeof periodCode === 'string'
        ? periodCode.toLowerCase().replace(/\s+/g, '_')
        : periodCode;

    let startDate, endDate;

    switch (normalizedCode) {
        case 'today':
            startDate = moment().startOf('day');
            endDate = moment();
            break;

        case 'this_week':
        case 'this week':
            startDate = moment().startOf('week');
            endDate = moment();
            break;

        case 'this_month':
        case 'this month':
            startDate = moment().startOf('month');
            endDate = moment();
            break;

        case 'last_week':
        case 'last week':
            startDate = moment().subtract(1, 'week').startOf('week');
            endDate = moment().subtract(1, 'week').endOf('week');
            break;

        case 'last_month':
        case 'last month':
            startDate = moment().subtract(1, 'month').startOf('month');
            endDate = moment().subtract(1, 'month').endOf('month');
            break;

        case 'last_3_months':
        case 'last 3 months':
            startDate = moment().subtract(3, 'months').startOf('month');
            endDate = moment().subtract(1, 'month').endOf('month');
            break;

        case 'last_6_months':
        case 'last 6 months':
            startDate = moment().subtract(6, 'months').startOf('month');
            endDate = moment().subtract(1, 'month').endOf('month');
            break;

        case 'last_12_months':
        case 'last 12 months':
        case 'last_year':
            startDate = moment().subtract(12, 'months').startOf('month');
            endDate = moment().subtract(1, 'month').endOf('month');
            break;

        case 'year_to_date':
        case 'year to date':
        case 'ytd':
            startDate = moment().startOf('year');
            endDate = moment();
            break;

        case 'lifetime': {
            const lifetimeStart = options.lifetimeStart ? moment(options.lifetimeStart) : null;
            if (!lifetimeStart || !lifetimeStart.isValid()) {
                return returnRange ? { startDate: null, endDate: null } : null;
            }
            startDate = lifetimeStart.startOf('day');
            endDate = moment();
            break;
        }

        case 'custom':
        case 'custom_date':
        case 'custom date':
            return returnRange ? { startDate: null, endDate: null } : null;

        default:
            startDate = moment().startOf('day');
            endDate = moment();
            break;
    }

    return returnRange
        ? { startDate: startDate.toDate(), endDate: endDate.toDate() }
        : startDate.toDate();
};

export const isCustomDatePeriod = (period) => {
    const periodCode = typeof period === 'object' && period?.code
        ? period.code
        : period;

    if (!periodCode) return false;

    const normalizedCode = typeof periodCode === 'string'
        ? periodCode.toLowerCase().replace(/\s+/g, '_')
        : periodCode;

    return ['custom', 'custom_date', 'custom date'].includes(normalizedCode);
};

export const isLifetimePeriod = (period) => {
    const periodCode = typeof period === 'object' && period?.code
        ? period.code
        : period;

    if (!periodCode || typeof periodCode !== 'string') return false;

    return periodCode.toLowerCase().replace(/\s+/g, '_') === 'lifetime';
};